import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errText } from '../lib/api';
import Markdown from '../components/Markdown';
import { SUBJECTS, LEVELS, subjectName, levelName, PROBLEM_STATUS } from '../lib/meta';

interface ProblemCard {
  _id: string;
  subject: string;
  level: string;
  stem: string;
  status: string;
  userId?: { username?: string };
  createdAt: string;
  solution?: { generatedAt?: string };
}

export default function ProblemsPage() {
  const [problems, setProblems] = useState<ProblemCard[]>([]);
  const [subject, setSubject] = useState('');
  const [level, setLevel] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (subject) params.subject = subject;
      if (level) params.level = level;
      const { data } = await api.get('/xyz/problems', { params });
      setProblems(data.data.problems);
      setErr('');
    } catch (e) {
      setErr(errText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, level]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm">
          <option value="">全部科目</option>
          {SUBJECTS.map((s) => <option key={s.code} value={s.code}>{s.icon} {s.name}</option>)}
        </select>
        <select value={level} onChange={(e) => setLevel(e.target.value)} className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm">
          <option value="">全部学段</option>
          {LEVELS.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
        </select>
        <Link
          to="/problems/new"
          className="ml-auto rounded-lg bg-brand-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
        >
          ＋ 发布题目
        </Link>
      </div>

      {err && <p className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{err}</p>}
      {loading ? (
        <p className="py-10 text-center text-sm text-gray-400">加载中…</p>
      ) : problems.length === 0 ? (
        <div className="py-16 text-center text-sm text-gray-400">
          还没有题目。点右上角「发布题目」，拍一张试试！
        </div>
      ) : (
        <div className="space-y-3">
          {problems.map((p) => {
            const st = PROBLEM_STATUS[p.status] || PROBLEM_STATUS.pending;
            return (
              <Link
                key={p._id}
                to={`/problems/${p._id}`}
                className="block rounded-xl border border-gray-100 bg-white p-4 shadow-sm hover:border-brand-300"
              >
                <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full bg-brand-50 px-2 py-0.5 font-medium text-brand-700">
                    {subjectName(p.subject)}
                  </span>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">{levelName(p.level)}</span>
                  <span className={`rounded-full px-2 py-0.5 font-medium ${st.cls}`}>{st.label}</span>
                  <span className="ml-auto text-gray-400">
                    {p.userId?.username || '匿名'} · {new Date(p.createdAt).toLocaleDateString('zh-CN')}
                  </span>
                </div>
                <div className="max-h-24 overflow-hidden text-sm leading-relaxed text-gray-800">
                  <Markdown text={p.stem.slice(0, 200)} className="text-sm" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
